import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { TaskGruopModel } from '../models/task-group.model';
import { JobModel } from '../models/job.model';
import { ConfigurationService } from '../shared/services/configuration.service';
import { Guid } from 'guid-typescript';
import { ScheduleCrmTaskModel } from '../models/schedule-cmr-task.model';
import { VisitModel } from '../models/visit.model';
import { CompletedFormModel } from '../models/completedForm.model';
import { TaskWithSubTasksModel } from '../models/task-subtasks.model';
import { map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class WorkTaskService {
  private readonly http = inject(HttpClient);
  private readonly configSvc = inject(ConfigurationService);

  private readonly backUrl;

  constructor() {
    this.backUrl = `${this.configSvc.workloadsApiUrl}api`;
  }

  getTaskGroups(): Observable<Array<TaskGruopModel>> {
    return this.http.get<Array<TaskGruopModel>>(`${this.backUrl}/TaskGroups`);
  }

  getJobs(id: Guid | string): Observable<Array<JobModel>> {
    return this.http.get<Array<JobModel>>(
      `${this.backUrl}/TaskGroups/${id}/jobs`
    );
  }

  getVisibleTaskGroups(): Observable<Array<TaskGruopModel>> {
    return this.http.get<Array<TaskGruopModel>>(
      `${this.backUrl}/taskGroups/visibles`
    );
  }

  getVisibleJobs(id: Guid | string): Observable<Array<JobModel>> {
    return this.http.get<Array<JobModel>>(
      `${this.backUrl}/taskGroups/${id}/jobs/visibles`
    );
  }

  getCrmTaskReportList(): Observable<ScheduleCrmTaskModel[]> {
    return this.http.get<ScheduleCrmTaskModel[]>(
      `${this.backUrl}/tasks/crmtasksreport`
    );
  }

  getCrmTaskReport(opportunityId: number): Observable<ScheduleCrmTaskModel> {
    return this.http.get<ScheduleCrmTaskModel>(
      `${this.backUrl}/tasks/crmtasksreport/${opportunityId}`
    );
  }

  getAvailableContractor(
    contractorId: string | undefined,
    scheduleDate: string,
    taskGroupId?: Guid
  ): Observable<boolean> {
    const encodedDate = encodeURIComponent(scheduleDate);
    return this.http.get<boolean>(
      `${this.backUrl}/contractors/${contractorId}/isAvailable?scheduleDate=${encodedDate}&taskGroupId=${taskGroupId}`
    );
  }

  getClientVisits(id: Guid | string): Observable<VisitModel[]> {
    return this.http.get<VisitModel[]>(
      this.backUrl + `/CompletionForms/Client/${id}`
    );
  }

  getClientVisitsReports(
    taskId: string,
    typeReport: number
  ): Observable<CompletedFormModel[]> {
    return this.http.get<CompletedFormModel[]>(
      this.backUrl +
        `/CompletionForms/relatedTasks?taskId=${taskId}&typeReport=${typeReport}`
    );
  }

  // Tasks and Sub tasks services
  getTaskWithSubTasks(): Observable<TaskWithSubTasksModel[]> {
    return this.http.get<TaskWithSubTasksModel[]>(`${this.backUrl}/TaskGroups`);
  }

  getTaskWithSubTasksById(taskId: string): Observable<TaskWithSubTasksModel> {
    return this.http.get<TaskWithSubTasksModel>(
      `${this.backUrl}/TaskGroups/${taskId}`
    );
  }

  createTaskAndSubtask(model: TaskWithSubTasksModel): Observable<any> {
    return this.http.post(`${this.backUrl}/TaskGroups`, model).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  updateTaskAndSubtask(
    taskId: string,
    model: TaskWithSubTasksModel
  ): Observable<any> {
    return this.http.patch(`${this.backUrl}/TaskGroups/${taskId}`, model).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  activateTaskGroup(taskId: string, isActive: boolean): Observable<any> {
    return this.http.delete(`${this.backUrl}/TaskGroups/${taskId}`, {
      body: {
        isActive: isActive,
      },
    });
  }

  activateTaskGroupJob(taskId: string, isActive: boolean): Observable<any> {
    return this.http.delete(`${this.backUrl}/TaskGroups/jobs/${taskId}`, {
      body: {
        isActive: isActive,
      },
    });
  }

  setVisibilityTaskGroupJob(
    taskId: string,
    isVisible: boolean
  ): Observable<any> {
    return this.http.patch(`${this.backUrl}/TaskGroups/${taskId}/visible`, {
      isVisible: isVisible,
    });
  }

  setVisibilitySubTaskGroupJob(
    subtaskId: string,
    isVisible: boolean
  ): Observable<any> {
    return this.http.patch(
      `${this.backUrl}/TaskGroups/jobs/${subtaskId}/visible`,
      {
        isVisible: isVisible,
      }
    );
  }
}
